import {Alert} from 'antd';
import {useMemo} from 'react';

import {useHelpLink} from '../../../../provider/help-links/help-links-hook';
import {
    isLocalPackInfoBannerVisible,
    LOCAL_PACK_INFO_BANNER_CONFIG_LINK_KEY,
    LocalPackInfoBannerContextServiceType,
    parseLocalPackInfoBannerHelpLinkConfig,
    resolveLocalPackInfoBannerMessage,
} from '../../../../provider/help-links/local-pack-info-banner-help-link-config';
import {useLocale} from '../../../../provider/locale/locale-hook';

import * as styles from './local-pack-info-banner.scss';

type LocalPackInfoBannerPropsType = {
    /**
     * Catalog + service pairs currently in context on the page (e.g. the active collection or the
     * selected catalog/service tab). A general banner (no `catalogs`/`services` in its config)
     * ignores this list and is always shown.
     */
    activeServices: Array<LocalPackInfoBannerContextServiceType>;
};

export function LocalPackInfoBanner(props: LocalPackInfoBannerPropsType): JSX.Element | null {
    const {activeServices} = props;
    const {shortLocaleName} = useLocale();
    const configLink = useHelpLink(LOCAL_PACK_INFO_BANNER_CONFIG_LINK_KEY, true);

    const config = useMemo(() => parseLocalPackInfoBannerHelpLinkConfig(configLink?.config), [configLink?.config]);

    const messages = useMemo(() => {
        if (!config) {
            return [];
        }

        return config.banners
            .filter((banner) => isLocalPackInfoBannerVisible(banner, activeServices))
            .map((banner) => resolveLocalPackInfoBannerMessage(banner.message, shortLocaleName))
            .filter((message): message is string => message !== null);
    }, [config, activeServices, shortLocaleName]);

    if (messages.length === 0) {
        return null;
    }

    return (
        <div className={styles.LocalPackInfoBanner}>
            {messages.map((message) => (
                <Alert key={message} message={message} showIcon type="warning" />
            ))}
        </div>
    );
}
